import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { TooltipDirective } from './tooltip/tooltip.directive';
import { ChannelChipChannel, ChannelChipComponent, ChannelChipState } from './channel-chip.component';

/** Visual tone of a {@link WhChipComponent}. Mirrors the integrator's original
 * `.wh-chip.tone-*` scale, minus the inline `<style>` block. */
export type WhChipTone = 'ok' | 'warn' | 'error' | 'idle' | 'blue';

/** One radio channel of a device in a {@link WhStatusStripComponent}: see {@link ChannelChipComponent}. */
export interface WhChannelSpec {
  channel: ChannelChipChannel;
  state: ChannelChipState;
  value?: string | null;
  bars?: number | null;
  title?: string;
  /** Epoch ms of the last frame on this channel; bumps the live dot's ping per position. */
  pulseKey?: number | null;
}

/** One chip in a {@link WhStatusStripComponent}. `label` is the (already
 * translated) caption, `value` the lighter trailing part — `BAT` + `66%`. */
export interface WhChipSpec {
  label: string;
  value?: string | null;
  tone: WhChipTone;
  /** CSS colour for a leading dot — used by the gateway strip's source swatches. */
  swatch?: string;
  title?: string;
}

const CHIP_TONE: Record<WhChipTone, string> = {
  // Light values are the integrator's originals so its rendering is unchanged;
  // the dark half is new (the old inline styles hardcoded `background:#fff`).
  ok: 'border-[#31c48d] text-[#03543f] dark:border-emerald-500/60 dark:text-emerald-300',
  warn: 'border-[#f0b429] text-[#723b13] dark:border-amber-500/60 dark:text-amber-300',
  error: 'border-[#f05252] text-[#9b1c1c] dark:border-red-500/60 dark:text-red-300',
  idle: 'border-gray-300 text-gray-700 dark:border-slate-600 dark:text-slate-300',
  blue: 'border-[#3f83f8] text-[#1e3a8a] dark:border-blue-500/60 dark:text-blue-300',
};

const STATUS_TONE: Record<string, string> = {
  ONLINE: 'bg-[#DEF7EC] text-[#03543F] dark:bg-emerald-500/15 dark:text-emerald-300',
  CHARGING: 'bg-[#FDF6B2] text-[#723B13] dark:bg-amber-500/15 dark:text-amber-300',
  OFFLINE: 'bg-[#FDE8E8] text-[#9B1C1C] dark:bg-red-500/15 dark:text-red-300',
};

const STATUS_TONE_DEFAULT = 'bg-[#F3F4F6] text-[#6B7280] dark:bg-slate-700 dark:text-slate-300';

/**
 * Gateway config/state values use a fixed numeric health scale
 * (1 = ok, 0 = idle, -1 = warn, -2 = error); anything non-numeric is an
 * informational string. Both apps had their own copy of this switch — the
 * integrator as `.wh-chip.tone-*` CSS, inside as `ConfigStateComponent`.
 */
export function whChipToneForState(value: unknown, type?: string): WhChipTone {
  if (type === 'string' || typeof value === 'string') return 'blue';
  switch (Number(value)) {
    case 1:
      return 'ok';
    case 0:
      return 'idle';
    case -1:
      return 'warn';
    case -2:
      return 'error';
    default:
      return 'idle';
  }
}

/**
 * The outlined uppercase chip used across device / gateway detail strips.
 * Single implementation for both apps — see {@link WhStatusStripComponent}.
 */
@Component({
  selector: 'wh-chip',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'contents' },
  template: `
    <span
      class="inline-flex items-center gap-1.5 whitespace-nowrap rounded border bg-white px-2.5 py-[3px] text-[11px] font-bold uppercase leading-[1.3] tracking-[0.02em] dark:bg-transparent"
      [class]="toneClass()"
      [title]="title() ?? ''">
      @if (swatch(); as color) {
        <span class="inline-block h-2.5 w-2.5 rounded-full border border-white/60" [style.background]="color"></span>
      }
      <span>{{ label() }}</span>
      @if (value() !== null && value() !== undefined && value() !== '') {
        <span class="font-medium opacity-80">{{ value() }}</span>
      }
    </span>
  `,
})
export class WhChipComponent {
  readonly label = input.required<string>();
  readonly value = input<string | number | null | undefined>(undefined);
  readonly tone = input<WhChipTone>('idle');
  readonly swatch = input<string | undefined>(undefined);
  readonly title = input<string | undefined>(undefined);

  protected readonly toneClass = computed(() => CHIP_TONE[this.tone()]);
}

/**
 * Device/gateway status strip: a tinted status block on the left, a wrapping
 * row of {@link WhChipComponent} on the right, both inside one bordered panel.
 *
 * Shared by wheere-inside and wheere-integrator so the two detail headers stay
 * pixel-identical — previously each app assembled its own badges from five
 * different components with five different colour scales.
 */
@Component({
  selector: 'wh-status-strip',
  standalone: true,
  imports: [WhChipComponent, ChannelChipComponent, TooltipDirective],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'block' },
  template: `
    <div
      class="flex items-stretch overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-slate-700 dark:bg-slate-800">
      <div
        class="flex flex-shrink-0 flex-col items-center justify-center px-5 py-2.5"
        [class]="statusToneClass()"
        [libTooltip]="statusTooltip()"
        libTooltipPosition="top">
        <span class="text-base font-bold uppercase leading-tight tracking-wider">{{ statusText() }}</span>
        @if (statusSubLabel(); as sub) {
          <span class="text-[11px] font-semibold uppercase tracking-wider opacity-75">{{ sub }}</span>
        }
      </div>
      <div class="flex min-w-0 flex-1 flex-col justify-center gap-1.5 px-3.5 py-2">
        <div class="flex flex-wrap items-center gap-1.5">
          @if (channels().length) {
            @for (ch of channels(); track ch.channel) {
              <lib-channel-chip
                [channel]="ch.channel"
                [state]="ch.state"
                [value]="ch.value"
                [bars]="ch.bars ?? null"
                [title]="ch.title"
                [pulseKey]="ch.pulseKey ?? null" />
            }
            <span class="mx-1 h-[18px] w-px bg-gray-200 dark:bg-slate-600" aria-hidden="true"></span>
          }
          @for (c of chips(); track c.label) {
            <wh-chip [label]="c.label" [value]="c.value" [tone]="c.tone" [swatch]="c.swatch" [title]="c.title" />
          }
        </div>
        @if (chipsSecondary().length) {
          <div class="flex flex-wrap items-center gap-1.5 border-t border-gray-100 pt-1.5 dark:border-slate-700">
            @for (c of chipsSecondary(); track c.label) {
              <wh-chip [label]="c.label" [value]="c.value" [tone]="c.tone" [swatch]="c.swatch" [title]="c.title" />
            }
          </div>
        }
      </div>
    </div>
  `,
})
export class WhStatusStripComponent {
  /** Raw status — drives the block's colour (ONLINE / CHARGING / OFFLINE). */
  readonly status = input<string | null | undefined>(undefined);
  /** Display text. Defaults to {@link status} when the caller has nothing translated. */
  readonly statusLabel = input<string | null | undefined>(undefined);
  /** Small line under the status word — gateways put their uptime there. */
  readonly statusSubLabel = input<string | null | undefined>(undefined);
  readonly statusTooltip = input<string>('');
  /** Radio channels (4G / BLE / LoRa) shown before {@link chips}, in the given order. Empty = hidden. */
  readonly channels = input<WhChannelSpec[]>([]);
  readonly chips = input<WhChipSpec[]>([]);
  /** Optional second row, divided from {@link chips}. Gateways split "config"
   * summary chips from the live per-key state; devices use one row only. */
  readonly chipsSecondary = input<WhChipSpec[]>([]);

  protected readonly statusText = computed(() => this.statusLabel() ?? this.status() ?? 'UNKNOWN');
  protected readonly statusToneClass = computed(() => STATUS_TONE[this.status() ?? ''] ?? STATUS_TONE_DEFAULT);
}
